import React, { useState } from 'react';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import FormInput from 'CommonComponents/FormInput';
import SelectInput from 'CommonComponents/SelectInput';

/**
 * A session by hand. The venue list holds only the venues the chosen event is
 * attached to, with a pointer to the Venues step for any venue not listed. onSubmit returns
 * a promise; validation errors are shown against their fields.
 */
export default function AddSessionForm({ types, events, venues, onSubmit, onCancel, onGoToVenues }) {
    const [values, setValues] = useState({
        schedule_type_id: types[0]?.id ?? '',
        sport_id: events[0]?.id ?? '',
        venue_id: '',
        name: '',
        date: '',
        start_time: '',
        end_time: '',
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const event = events.find((item) => String(item.id) === String(values.sport_id));
    const eventVenues = venues.filter((venue) => event?.venue_ids.includes(venue.id));
    const venueId = eventVenues.some((venue) => String(venue.id) === String(values.venue_id)) ? values.venue_id : (eventVenues[0]?.id ?? '');
    const venue = eventVenues.find((item) => String(item.id) === String(venueId));

    const set = (field, value) => {
        setErrors((current) => ({ ...current, [field]: undefined }));
        setValues((current) => ({ ...current, [field]: value }));
    };

    const submit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});

        onSubmit({ ...values, venue_id: venueId, name: values.name.trim() })
            .catch((err) => {
                const fieldErrors = err?.response?.data?.errors;
                if (fieldErrors) {
                    setErrors(Object.fromEntries(Object.entries(fieldErrors).map(([field, messages]) => [field, [].concat(messages).join(' ')])));
                }
            })
            .finally(() => setSubmitting(false));
    };

    const select = (field, label, options, value = values[field]) => (
        <SelectInput
            id={`setup-wizard-session-${field}`}
            name={`setup-wizard-session-${field}`}
            label={label}
            value={value}
            disabled={submitting}
            showError={!!errors[field]}
            errorText={errors[field]}
            onChange={(e) => set(field, e.target.value)}
        >
            {options.map((option) => (
                <option key={option.id} value={option.id}>
                    {option.name}
                </option>
            ))}
        </SelectInput>
    );

    const input = (field, label, props = {}) => (
        <FormInput
            id={`setup-wizard-session-${field}`}
            name={`setup-wizard-session-${field}`}
            label={label}
            value={values[field]}
            readOnly={submitting}
            showError={!!errors[field]}
            errorText={errors[field]}
            onChange={(e) => set(field, e.target.value)}
            {...props}
        />
    );

    return (
        <form
            className="tw-flex tw-flex-col tw-gap-y-3 tw-w-full tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-border-charcoal-15 tw-bg-background"
            onSubmit={submit}
        >
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                {select('sport_id', $t('Event'), events)}
                {eventVenues.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        {select('venue_id', $t('Venue'), eventVenues, venueId)}
                        <p className="tw-mb-0 tw-text-sm tw-text-charcoal-75">
                            {$t('Venue not listed? Attach {{event}} to that venue first.', { event: event?.name ?? '' })}{' '}
                            <button type="button" className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-blue hover:tw-underline" onClick={onGoToVenues}>
                                {$t('Go to the Venues step')}
                            </button>
                        </p>
                    </div>
                ) : (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <span className="text-input__label">{$t('Venue')}</span>
                        <p className="tw-mb-0 tw-text-sm tw-text-charcoal-75">
                            {$t('{{event}} is not attached to a venue yet.', { event: event?.name ?? '' })}{' '}
                            <button type="button" className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-blue hover:tw-underline" onClick={onGoToVenues}>
                                {$t('Attach it on the Venues step')}
                            </button>
                        </p>
                        {errors.venue_id && <div className="tw-text-red-500 tw-text-sm">{errors.venue_id}</div>}
                    </div>
                )}
                {select('schedule_type_id', $t('Session type'), types)}
                {input('name', $t('Session name'), { maxLength: 191, autoFocus: true })}
                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label htmlFor="setup-wizard-session-date" className="text-input__label">
                        {$t('Date')}
                    </label>
                    <DatePickerField
                        id="setup-wizard-session-date"
                        name="date"
                        valueFormat="yyyy-MM-dd"
                        className={errors.date ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                        value={values.date}
                        min={venue?.hire_start}
                        max={venue?.hire_end}
                        disabled={submitting}
                        onChange={set}
                    />
                    {errors.date && <div className="tw-text-red-500 tw-text-sm">{errors.date}</div>}
                </div>
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                    {input('start_time', $t('Start time'), { type: 'time' })}
                    {input('end_time', $t('End time'), { type: 'time' })}
                </div>
            </div>
            <div className="tw-flex tw-gap-2">
                <Button type="submit" variant="primary" small loading={submitting} disabled={submitting || !venueId}>
                    {$t('Save')}
                </Button>
                <Button variant="outline" small disabled={submitting} onClick={onCancel}>
                    {$t('Cancel')}
                </Button>
            </div>
        </form>
    );
}
